"use client";

import { useEffect, useEffectEvent, useRef, useState, useMemo } from "react";

import { datadogRum } from "@datadog/browser-rum";
import { getIdentityVerification } from "@peerspace/bellhop";
import { deleteCookie, getCookie, setCookie } from "cookies-next";
import isNull from "lodash/isNull";
import { FormattedMessage, useIntl } from "react-intl";

import { Divider } from "@/components/atoms/Divider";
import { Text } from "@/components/atoms/Text";
import { Toaster } from "@/components/molecules/Toast";
import CheckIcon from "@/icons/__generated__/Check/Check20";
import {
  useListingDataForNormalization,
  normalizeListingObj,
} from "src/app/analytics/normalize-listing";
import { useClientEnvironment } from "src/app/environment/client";
import { AboutTheSpace } from "~/components/listings/about-the-space";
import AddOns from "~/components/listings/add-ons";
import { BookListingCard } from "~/components/listings/book-listing/BookListingCard/BookListingCard";
import { BookListingContextProvider } from "~/components/listings/book-listing/BookListingContext";
import { MobileBookListingSection } from "~/components/listings/book-listing/MobileBookListingSection/MobileBookListingSection";
import Cancellation from "~/components/listings/cancellation";
import EventFaq from "~/components/listings/faqs/EventFaq";
import { surveillanceText } from "~/components/listings/faqs/faq-utils";
import ProductionFaq from "~/components/listings/faqs/ProductionFaq";
import { FsvCreditAccordion } from "~/components/listings/full-service-venues/components/FsvCreditAccordion";
import { FsvSection } from "~/components/listings/full-service-venues/components/FsvSection";
import GoogleMap from "~/components/listings/google-map";
import { HealthAndSafety } from "~/components/listings/health-and-safety";
import { HeroSection } from "~/components/listings/hero-section/HeroSection";
import { HostDetails } from "~/components/listings/host-details";
import { ListingAmenities } from "~/components/listings/listing-amenities";
import { ListingDetailsWithIcons } from "~/components/listings/listing-details-with-icons";
import { MobileFooter } from "~/components/listings/mobile-footer";
import NavbarPage from "~/components/listings/navbar-page";
import OpHoursComp from "~/components/listings/operating-hours";
import { PendingListingBanner } from "~/components/listings/pending-listing-banner";
import RecommendedListings from "~/components/listings/recommended-listings";
import { RecommendedVendors } from "~/components/listings/RecommendedVendors/RecommendedVendors";
import ReviewHighlights from "~/components/listings/review-highlights";
import Reviews from "~/components/listings/reviews";
import { ShareNudgeAlert } from "~/components/listings/ShareNudgeAlert";
import { UgcCarousel } from "~/components/listings/ugc-carousel/ugc-carousel";
import { VendorAlert } from "~/components/listings/vendor-alert";
import { SaveListingInfo } from "~/components/save/SaveListing.types";
import { StripeContextProvider } from "~/components/stripe";
import { useEnvData } from "~/context/EnvDataContext";
import { useListingData } from "~/context/ListingPageDataContext";
import { useIntercom } from "~/hooks/use-intercom";
import { useShareNudgeAlert } from "~/hooks/use-share-nudge-alert";
import { useStripePublicKey } from "~/hooks/use-stripe-public-key";
import useRedirectToSigninSignup from "~/hooks/useRedirectToSigninSignup";
import { AppModalKey, useAppModal, useShareModal } from "~/modals";
import { useProfileData } from "~/queries/profile/useProfile";
import { useStatsigListingPageUserUpdate } from "~/statsig/useStatsigListingPageUserUpdate";
import { IAttachment } from "~/types/api-types";
import { ACCESS_TOKEN_COOKIE, PSSK_COOKIE } from "~/utils/constants";
import { createCookieOptions } from "~/utils/cookie-utils";

import { useInquiryRecommendationsModal } from "./_hooks/useInquiryRecommendationsModal";
import { useTransactableModals } from "./_hooks/useTransactableModals";
export const ListingClient = () => {
  useStatsigListingPageUserUpdate();
  useTransactableModals();
  const { BRANCH_SDK_KEY, STATSIG_SDK_CLIENT_KEY } = useClientEnvironment();
  const { openModal: openShareModal } = useShareModal();
  const { data: listingPageData } = useListingData();
  const {
    allAttachments,
    amenities,
    attendeesMin = 0,
    cancellationPolicy,
    category,
    cityGoogle,
    cleaningInfo,
    emailShare,
    eventFaqDetails,
    fbShare,
    fullService,
    hasSurveillance,
    images,
    inventory,
    listingAwards,
    listingCapacity,
    listingCity,
    listingCountry,
    listingId,
    listingMinimumDuration,
    listingOwnerFirstName,
    listingRules,
    listingState,
    listingStatus,
    listingStructuredReviews,
    listingVenueName,
    mapUrl,
    metro,
    minAge,
    neighborhood,
    neighborhoodBreadcrumb,
    opHours,
    pageTitle,
    parkingDetails,
    productionDetails,
    recommendedListings = [],
    reviews,
    spaceReviewCount,
    spaceReviewStars,
    spaceType,
    sqft,
    stateGoogle,
    surveillanceDescription,
    title,
    twitterShare,
    useType,
    useTypeBreadcrumb,
    vendors,
  } = listingPageData;
  const profile = useProfileData();
  const env = useEnvData();

  useInquiryRecommendationsModal({
    listingCity,
    listingCountry,
    listingState,
    neighborhood,
    recommendedListings: recommendedListings.slice(0, 5),
    useType,
  });

  useEffect(() => {
    window.analyticsIntegrations.listingsProductView(listingPageData);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const intl = useIntl();
  const {
    isModalOpen: isSaveListingModalOpen,
    modalProps: saveListingModalProps,
    openModal: openSaveListingModal,
  } = useAppModal(AppModalKey.ListingSaveModal);
  const [guestNeedsVerification, setGuestNeedsVerification] = useState<
    boolean | undefined
  >(undefined);
  // Seeded from the server data, not only by the effect below, so a save flow opened
  // in the first commit (return from login) already gets the guest's attachments.
  const [attachments, setAttachments] = useState<IAttachment[]>(
    allAttachments ?? [],
  );
  const [displaySuccessBanner, setDisplaySuccessBanner] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const BranchSDK = (await import("branch-sdk")).default;

        BranchSDK.addListener(undefined, (event) => {
          if (event === "didShowJourney") {
            const banner = document.getElementById("branch-banner-iframe");
            document.addEventListener("click", (event) => {
              if (
                banner &&
                !banner.contains(event.target as Node) &&
                window.branch
              ) {
                window.branch.closeJourney();
              }
            });
          }
        });

        BranchSDK.init(BRANCH_SDK_KEY, {}, (err, data) => {
          if (err) {
            datadogRum.addError(err);
            return;
          }

          const dataParsed = data?.data_parsed as
            Record<string, unknown> | undefined;
          const shareType = dataParsed?.share_type;

          if (shareType) {
            BranchSDK.track("pageview", { share_type: shareType });
          }
        });
      } catch (e) {
        datadogRum.addError(e);
      }
    })();
  }, [BRANCH_SDK_KEY]);

  useIntercom();

  useEffect(() => {
    if (displaySuccessBanner) {
      setTimeout(() => {
        setDisplaySuccessBanner(false);
      }, 3000);
    }
  }, [displaySuccessBanner]);

  useEffect(() => {
    const accessToken = getCookie(ACCESS_TOKEN_COOKIE) as string;
    const ssoId = profile?.SSOid;

    // Get guest IDV info
    if (accessToken && guestNeedsVerification === undefined && ssoId) {
      (async () => {
        const res = await getIdentityVerification({
          sso_id: ssoId,
          listing_id: listingId,
        });
        const guestNeedsVerification =
          res?.data?.guest_needs_verification || false;

        setGuestNeedsVerification(guestNeedsVerification);
      })();
    }

    // If user logs out, clear IDV info
    if (!ssoId && guestNeedsVerification !== undefined) {
      setGuestNeedsVerification(undefined);
    }
  }, [guestNeedsVerification, listingId, profile?.SSOid]);

  // Set metro cookie if it's different from the current metro
  useEffect(() => {
    const isSameMetro = metro === getCookie("PSmetro");
    if (metro && !isSameMetro) {
      const cookieOptions = createCookieOptions({
        expires: new Date(Date.now() + 3888000000), // 45 days
      });
      setCookie("PSmetro", metro, cookieOptions);
    }
  }, [metro]);

  // Set use type cookie
  useEffect(() => {
    const isSameUseType = useType === getCookie("PSUT");
    if (useType && !isSameUseType) {
      const cookieOptions = createCookieOptions({
        expires: new Date(Date.now() + 3888000000), // 45 days
      });
      setCookie("PSUT", useType, cookieOptions);
    }
  }, [useType]);

  useEffect(() => {
    // set attachments so we can use toggle it in save listing flow
    if (allAttachments) {
      setAttachments(allAttachments);
    }
  }, [allAttachments]);

  const mobileBkgCtasRef = useRef<HTMLDivElement>(null);
  const [isMobileFooterVisible, setIsMobileFooterVisible] = useState(false);

  useEffect(() => {
    if (mobileBkgCtasRef.current) {
      const observer = new IntersectionObserver(
        ([entry]) => {
          const rect = entry.boundingClientRect;
          const root = entry.rootBounds;
          const isSectorVisible = 0 < (root?.top ?? 0) - rect.top;

          if (!entry.isIntersecting && isSectorVisible) {
            setIsMobileFooterVisible(true);
          } else {
            setIsMobileFooterVisible(false);
          }
        },
        {
          rootMargin: "80px",
          threshold: 1.0,
        },
      );
      observer.observe(mobileBkgCtasRef.current);
      return () => observer.disconnect();
    }
  }, [mobileBkgCtasRef]);

  const listingHasVendors = Boolean(vendors?.length);

  const listingData = useListingDataForNormalization();
  const listing = useMemo(
    () => normalizeListingObj(listingData),
    [listingData],
  );

  const showSuccessBanner = () => {
    setDisplaySuccessBanner(true);
  };

  // Hands the flow the page's attachments; the effect below keeps them current while
  // it is open.
  const openSaveListingFlow = (listingInfo: SaveListingInfo) => {
    openSaveListingModal({
      altAnalyticsOrigin: getCookie("listing_nudge_save")
        ? "save listing details nudge"
        : "",
      attachments,
      listingData: listingPageData,
      listingInfo,
      onClose: () => deleteCookie("listing_nudge_save"),
      setAttachments,
      showSuccessBanner,
    });
  };

  // An effect event, so the return-from-login effect below opens the flow with the
  // latest attachments without re-running on them; the opener is new every render.
  const reopenPendingSaveFlow = useEffectEvent((listingInfo: SaveListingInfo) =>
    openSaveListingFlow(listingInfo),
  );

  // An effect event, so it reads the flow's latest props; the opener is new every render.
  // Gated on open: closing keeps the props for the exit animation, and the flow sets
  // attachments then closes in one handler, so syncing then would reopen it.
  const syncSaveListingAttachments = useEffectEvent(() => {
    if (
      isSaveListingModalOpen &&
      saveListingModalProps &&
      saveListingModalProps.attachments !== attachments
    ) {
      openSaveListingModal({ ...saveListingModalProps, attachments });
    }
  });

  // The flow's props are a snapshot taken on open, and it can open before the streamed
  // `allAttachments` arrive (e.g. on return from login); its own saves change them too.
  // It can also open with stale ones: the gallery modals hold on to an older
  // `handleToggleFavorite`, so re-checking when the flow's own attachments change
  // corrects it, whoever opened it.
  useEffect(() => {
    syncSaveListingAttachments();
  }, [attachments, saveListingModalProps?.attachments]);

  const trackReviewsAction = (e: React.MouseEvent): void => {
    const psskCookie = getCookie(PSSK_COOKIE) as string;
    const properties = {
      listing_id: listingId,
      action: e.target ? "Click Rating" : "Scroll",
      label: e.target ? "Click Rating" : "Scroll",
      category: `${useType}/${metro}/${neighborhood}`,
      activity: psskCookie,
      space_review_count: spaceReviewCount,
      space_review_stars: spaceReviewStars,
    };
    if ("Click Rating" === properties.action) {
      // the id="reviews" is from the reviews.tsx component
      document.getElementById("reviews")?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }
    window.analyticsIntegrations.trackEvent("reviews viewed", properties);
  };

  const visibleAddOns = (inventory?.inventory || []).filter(
    (item) => item.visible_on_listing,
  );

  const { handleRedirectToSignupPage, isUserLoggedIn } =
    useRedirectToSigninSignup();
  const stripePublicKey = useStripePublicKey(listingCountry);

  const handleToggleFavorite = () => {
    if (!isUserLoggedIn) {
      setCookie("listing_pending_save", listingId);
      handleRedirectToSignupPage("fav");
    } else {
      openSaveListingFlow({
        listingId: listingId,
        listingImg: images[0]?.externalId || "",
        listingTitle: title,
        listingLocation: neighborhood,
        listingCapacity: listingCapacity,
      });
    }
  };

  const handleNudgeSaveClick = () => {
    setCookie("listing_nudge_save", "true");
    handleToggleFavorite();
  };

  useEffect(() => {
    const savedPendingListingId = getCookie("listing_pending_save");
    if (!savedPendingListingId || !isUserLoggedIn) {
      return;
    }

    // The pending save can reference either the current listing or one of the
    // recommended listings (favorited from a recommended card while logged out).
    const isCurrentListing = savedPendingListingId === listingId;
    const recommendedPendingListing =
      recommendedListings?.find(
        (recommendedListing) =>
          recommendedListing._id === savedPendingListingId,
      ) ?? null;

    deleteCookie("listing_pending_save");

    // If the saved id matches neither the current listing nor any recommended
    // one, we have no data to populate the flow with, stop here
    if (!isCurrentListing && !recommendedPendingListing) {
      return;
    }

    // The pending save was recorded before a full-page auth redirect, so we
    // reopen the flow once the user returns logged in.
    reopenPendingSaveFlow({
      listingId: recommendedPendingListing?._id ?? listingId ?? "",
      listingImg:
        recommendedPendingListing?.images?.[0]?.external_id ??
        images?.[0]?.externalId ??
        "",
      listingTitle: recommendedPendingListing?.title ?? title ?? "",
      listingLocation:
        recommendedPendingListing?.neighborhood ?? neighborhood ?? "",
      listingCapacity:
        recommendedPendingListing?.number_guests || listingCapacity || 0,
    });
  }, [
    images,
    isUserLoggedIn,
    listingCapacity,
    listingId,
    neighborhood,
    recommendedListings,
    title,
  ]);

  const shareNudge = useShareNudgeAlert({
    attachments,
    listingId,
    listing,
    onSaveClick: handleNudgeSaveClick,
    onShareClick: () =>
      openShareModal({ emailShare, fbShare, twitterShare, variant: "listing" }),
  });

  return (
    <div>
      {displaySuccessBanner && (
        <div
          className="tw-fixed tw-top-0 tw-left-0 tw-w-full tw-h-[50px] tw-bg-[#3CB78B]
          tw-z-max tw-flex tw-items-center tw-justify-center
          tw-transform tw-transition-all tw-duration-500
          tw-translate-y-0 tw-opacity-100"
        >
          <div className="tw-text-center tw-flex">
            <CheckIcon className="tw-text-base-white tw-mr-8" />
            <Text className="tw-text-base-white" typeface="body-2">
              <FormattedMessage
                id="listing.project_share_form.invite"
                defaultMessage="Your invite was sent"
              />
            </Text>
          </div>
        </div>
      )}
      <NavbarPage
        attachments={attachments}
        hasAddons={!!inventory?.inventory?.length}
        hasReviews={!!reviews?.length}
        showRecommendedVendors={listingHasVendors}
        toggleFavorite={handleToggleFavorite}
      />
      {listingStatus !== "ACTIVATED" &&
        listingStatus !== "PAUSED" &&
        listingStatus !== "PAUSED_INTERNALLY" && (
          <PendingListingBanner
            listingId={listingId}
            listingStatus={listingStatus}
          />
        )}
      <HeroSection
        title={title}
        listingCity={listingCity}
        listingState={listingState}
        listingCountry={listingCountry}
        cityGoogle={cityGoogle}
        stateGoogle={stateGoogle}
        neighborhood={neighborhood}
        neighborhoodBreadcrumb={neighborhoodBreadcrumb}
        useTypeBreadcrumb={useTypeBreadcrumb}
        listingId={listingId}
        fbShare={fbShare}
        twitterShare={twitterShare}
        emailShare={emailShare}
        useType={useType}
        metro={metro}
        category={category as string}
        listingVenueName={listingVenueName}
        images={images}
        listingAwards={listingAwards}
        toggleFavorite={handleToggleFavorite}
        isFav={
          !!attachments.find((attachment) => attachment.value === listingId)
        }
      />
      <div className="tw-w-full tw-text-gray-900 ps-container-lg-margin ps-container-fixed">
        <div className="ps-row">
          <div className="tw-flex tw-flex-wrap sm:tw-pt-32 col-xs-12">
            {/* Listing Details */}
            <ListingDetailsWithIcons
              spaceReviewStars={spaceReviewStars}
              spaceReviewCount={spaceReviewCount}
              capacity={listingCapacity}
              sqft={sqft}
              minimum_duration={listingMinimumDuration}
              trackReviewsAction={trackReviewsAction}
              isFullServiceVenue={fullService?.active ?? false}
              attendeesMin={attendeesMin}
            />

            {/* About the Space */}
            <div className="lg:tw-w-2/3 tw-w-full tw-order-3 lg:tw-order-2 lg:tw-pr-16">
              {fullService?.active && (
                <>
                  <FsvSection />
                  <Divider spacing={0} className="tw-mt-24" />
                </>
              )}
              <HostDetails />
              {listingHasVendors && <VendorAlert />}
              {reviews && reviews.length > 3 && !fullService?.active && (
                <>
                  <ReviewHighlights
                    reviews={reviews.slice(0, 3)}
                    trackReviewsAction={trackReviewsAction}
                    listingId={listingId}
                  />
                  <Divider className="tw-w-full tw-text-gray-200" />
                </>
              )}

              <AboutTheSpace />

              {fullService?.active && (
                <div className="tw-scroll-m-72" id="how-credit-works">
                  <FsvCreditAccordion />
                </div>
              )}

              {!!eventFaqDetails && useType === "Event" && (
                <EventFaq
                  faqData={eventFaqDetails}
                  userID={profile?.SSOid || ""}
                  listingID={listingId}
                />
              )}

              {(!!listingRules || parkingDetails) && (
                <ProductionFaq
                  urlPrefix={env.currentLocaleData.data.urlPrefix || ""}
                  parkingData={
                    parkingDetails || {
                      is_available: false,
                      types: [],
                      description: "",
                    }
                  }
                  listingRules={{
                    rules: listingRules,
                    min_age: minAge,
                    has_surveillance:
                      hasSurveillance !== false &&
                      intl.formatMessage(surveillanceText.has_surveillance),
                    surveillance_description: isNull(hasSurveillance)
                      ? intl.formatMessage(surveillanceText.has_surveillance)
                      : surveillanceDescription,
                    ...productionDetails,
                  }}
                  userID={profile?.SSOid || ""}
                  listingID={listingId}
                />
              )}

              <ListingAmenities
                amenities={amenities}
                ownerFirstName={listingOwnerFirstName}
                listingId={listingId}
              />

              {!!vendors?.length && (
                <RecommendedVendors
                  vendors={vendors}
                  listingId={listingId}
                  useType={useType}
                />
              )}

              {/* Host Add Ons */}
              {visibleAddOns.length > 0 && (
                <AddOns addonItems={visibleAddOns} />
              )}

              {/* Location */}
              {!!mapUrl && (
                <>
                  <Divider className="tw-w-full tw-text-gray-200 tw-my-0" />
                  <GoogleMap mapUrl={mapUrl} />
                </>
              )}

              {/* {fullService?.active && (
                <HostDetailsDemotedBottom />
              )} */}

              {/* Operating Hours */}
              {!!opHours && <OpHoursComp opHours={opHours.operating_hours} />}

              <Divider className="tw-w-full tw-text-gray-200 tw-my-0" />
              <HealthAndSafety cleaningInfo={cleaningInfo} />

              {cancellationPolicy && (
                <Cancellation cancellationPolicy={cancellationPolicy} />
              )}

              {/* Reviews */}
              {reviews && !!reviews.length && (
                <>
                  <Divider className="tw-w-full tw-text-gray-200 tw-my-0" />
                  <Reviews reviews={reviews} />
                </>
              )}
            </div>
            {/* Fee Calc */}
            <div className="lg:tw-w-1/3 tw-w-full tw-order-2 lg:tw-order-3 tw-mb-32 lg:tw-pl-16">
              <BookListingContextProvider>
                <StripeContextProvider stripeKey={stripePublicKey ?? undefined}>
                  <BookListingCard
                    guestNeedsVerification={guestNeedsVerification}
                    faqs={eventFaqDetails}
                    pageTitle={pageTitle}
                  />

                  <div
                    className="tw-block lg:tw-hidden tw-mt-16"
                    ref={mobileBkgCtasRef}
                  >
                    <MobileBookListingSection
                      faqs={eventFaqDetails}
                      pageTitle={pageTitle}
                      guestNeedsVerification={guestNeedsVerification}
                    />
                  </div>
                </StripeContextProvider>
              </BookListingContextProvider>
            </div>
          </div>
        </div>
      </div>

      {listingStructuredReviews && !!listingStructuredReviews.length && (
        <UgcCarousel
          listingId={listingId}
          spaceType={spaceType}
          structuredReviews={listingStructuredReviews}
        />
      )}

      {recommendedListings?.length > 0 && (
        <RecommendedListings
          listingId={listingId}
          recommendedListings={recommendedListings}
          city={listingCity}
          useType={useType}
          allAttachments={attachments || []}
          openSaveListingFlow={openSaveListingFlow}
        />
      )}

      <div className="tw-block lg:tw-hidden">
        <MobileFooter
          listingId={listingId}
          isFav={
            !!attachments.find((attachment) => attachment.value === listingId)
          }
          toggleFavorite={handleToggleFavorite}
          isVisible={isMobileFooterVisible}
          faqs={eventFaqDetails}
          pageTitle={pageTitle}
        />
      </div>
      <ShareNudgeAlert
        isVisible={shareNudge.isVisible}
        message={shareNudge.alertMessage}
        ctaBtnText={shareNudge.ctaBtnText}
        onDismiss={shareNudge.onDismiss}
        onShareClick={shareNudge.onShareClick}
        experimentName={shareNudge.experimentName}
      />
      <Toaster
        viewportProps={{
          className: "tw-pointer-events-none tw-bottom-[62px] md:tw-bottom-0",
        }}
      />
    </div>
  );
};
